
import React, { useMemo } from 'react';
import { Form } from '@/components/ui/form';
import { UseFormReturn } from 'react-hook-form';
import { RegistrationFormValues } from '@/schemas/registrationSchema';
import { motion } from 'framer-motion';
import { BasicInfoRow, ContactInfoRow } from './FormRows';
import ClassYearImageSection from './ClassYearImageSection';
import ContributionSection from './ContributionSection';
import JobInfoSection from './JobInfoSection';
import TShirtSection from './TShirtSection';
import SubmissionButton from './SubmissionButton';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { useIsMobile } from '@/hooks/use-mobile';

interface FormContentProps {
  formRef: React.RefObject<HTMLFormElement>;
  formMethods: UseFormReturn<RegistrationFormValues>;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onYearChange: (year: string | null) => void;
  isSubmitting: boolean;
  isRestoringData: boolean;
}

const FormContent: React.FC<FormContentProps> = ({
  formRef,
  formMethods,
  onSubmit,
  onYearChange,
  isSubmitting,
  isRestoringData
}) => {
  const { control, watch } = formMethods;
  const isMobile = useIsMobile();
  
  // Watch the class field directly to avoid memoization issues
  const selectedClassYear = watch('class');
  
  // Memoize components to prevent unnecessary re-renders
  const loadingMessage = useMemo(() => {
    if (!isRestoringData) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex items-center justify-center p-4 mb-4 bg-blue-50 rounded-md"
      >
        <LoadingSpinner size="sm" color="primary" className="mr-2" />
        <span className="text-blue-600">Đang khôi phục dữ liệu...</span>
      </motion.div>
    );
  }, [isRestoringData]);
  
  const basicInfoSection = useMemo(() => (
    <BasicInfoRow 
      control={control} 
      onYearChange={onYearChange}
    />
  ), [control, onYearChange]);
  
  // Modified layout to be more efficient on mobile
  const contactAndImageSection = useMemo(() => {
    if (isMobile) {
      return (
        <div className="space-y-4">
          {selectedClassYear && (
            <ClassYearImageSection selectedClassYear={selectedClassYear} />
          )}
          <ContactInfoRow control={control} />
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-h-[300px]">
        <div className="md:col-span-1">
          <ContactInfoRow control={control} />
        </div>
        <div className="md:col-span-1 h-[300px]">
          <ClassYearImageSection selectedClassYear={selectedClassYear || null} />
        </div>
      </div>
    );
  }, [control, selectedClassYear, isMobile]);

  // Job info section
  const jobInfoSection = useMemo(() => (
    <JobInfoSection control={control} />
  ), [control]);
  
  // T-shirt section - ensure it's properly memoized and included
  const tshirtSection = useMemo(() => (
    <TShirtSection control={control} />
  ), [control]);
  
  const contributionSection = useMemo(() => (
    <ContributionSection control={control} />
  ), [control]);
  
  return (
    <Form {...formMethods}>
      {loadingMessage}
      <motion.form
        ref={formRef}
        onSubmit={onSubmit}
        className="space-y-6 md:space-y-8"
        initial={{ opacity: 0.95 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {basicInfoSection}
        {contactAndImageSection}
        {jobInfoSection}
        {tshirtSection}
        {contributionSection}
        <SubmissionButton isSubmitting={isSubmitting} />
      </motion.form>
    </Form>
  );
};

export default React.memo(FormContent);
